/** Illustrative us-east-1-ish defaults; operators override per deployment. */
export const DEFAULT_COST_RATES = {
    perEmail: 0.0001, // $0.10 / 1,000 messages
    perGbStorageMonth: 0.023,
    perDedicatedIpMonth: 24.95,
    perTbScanned: 5.0, // Athena: $5 / TB scanned
    /**
     * AWS Backup warm storage, $/GB-month (#321).
     *
     * Billed per RECOVERY POINT, not per resource: a plan keeping 35 daily points
     * holds 35 of them. DynamoDB backups are incremental, so those points are
     * mostly deltas rather than 35 full copies — but it is still a multiple of
     * the data size, which is the part operators are surprised by.
     */
    perGbBackupMonth: 0.10,
};
const BYTES_PER_GB = 1_073_741_824;
const BYTES_PER_TB = 1_099_511_627_776;
export function estimateCost(inputs, rates) {
    const email = inputs.emailsSent * rates.perEmail;
    // Each line is omitted rather than zero when its input is absent: a $0.00
    // column implies the charge was measured and came to nothing, which is a
    // different claim from "nobody measured it" (#370) or "this deployment does
    // not take backups" (#321). Omitted lines are left out of the total, so the
    // total is a floor over what was measured, not a guess at the rest.
    const line = (value, cost) => value === undefined ? undefined : cost(value);
    const storage = line(inputs.storageBytes, (v) => (v / BYTES_PER_GB) * rates.perGbStorageMonth);
    const dedicatedIp = line(inputs.dedicatedIps, (v) => v * rates.perDedicatedIpMonth);
    const athena = line(inputs.athenaBytesScanned, (v) => (v / BYTES_PER_TB) * rates.perTbScanned);
    const backup = line(inputs.backupBytes, (v) => (v / BYTES_PER_GB) * rates.perGbBackupMonth);
    return {
        email,
        ...(storage === undefined ? {} : { storage }),
        ...(dedicatedIp === undefined ? {} : { dedicatedIp }),
        ...(athena === undefined ? {} : { athena }),
        ...(backup === undefined ? {} : { backup }),
        total: email + (storage ?? 0) + (dedicatedIp ?? 0) + (athena ?? 0) + (backup ?? 0),
    };
}
/** Sum "emailsSent" from a set of campaigns' hot counters (our own metric). */
export function sumEmailsSent(campaigns) {
    return campaigns.reduce((n, c) => n + (c.counters?.sent ?? 0), 0);
}
/** The billing period ("YYYY-MM") an ISO-8601 instant falls in. Storage is UTC (§4.21). */
export function usagePeriodOf(iso) {
    return iso.slice(0, 7);
}
/**
 * Emails actually sent inside one billing period (#199).
 *
 * `sumEmailsSent` above folds campaign COUNTERS, which are lifetime totals: used
 * for a month it reports every email the org has ever sent, and reports it again
 * next month. Metering needs a period-scoped figure, and the append-only event
 * log is the only thing that carries a timestamp per send.
 */
export function emailsSentInPeriod(events, period) {
    let n = 0;
    for (const e of events)
        if (e.type === "sent" && usagePeriodOf(e.at) === period)
            n++;
    return n;
}
/**
 * A stored usage record as it should be READ (#370): legacy zeros become absent.
 *
 * Rows written before `awsFiguresMeasured` existed carry storage, dedicated-IP
 * and Athena figures of 0 that nobody measured: the old meter fell back to
 * `?? 0` and the old `estimateCost` always wrote all three cost lines. Only the
 * current period is ever re-metered, so without this every closed month — the
 * history someone would charge back on — kept showing $0.00 beside a current
 * month showing "not measured" for the same state.
 *
 * On a row without the marker, a zero is therefore unmeasured and is dropped;
 * a nonzero value can only have come from an ingest and is kept. A cost line is
 * dropped whenever it is zero, even if its input is missing, because an old
 * ingest that omitted Athena still got `cost.athena: 0`. `total` needs no
 * recompute: it only loses zeros. Backup is untouched — it was always optional.
 *
 * The same rule decides what `meterOrgUsage` carries forward, so the read and
 * the write cannot disagree, and the Usage screen applies it too. Pure and
 * idempotent; generic so the admin app's own response type passes through.
 */
export function readUsageRecord(record) {
    if (record.awsFiguresMeasured)
        return record;
    const out = { ...record, cost: { ...record.cost } };
    for (const key of ["storageBytes", "dedicatedIps", "athenaBytesScanned"]) {
        if (out[key] === 0)
            delete out[key];
    }
    for (const key of ["storage", "dedicatedIp", "athena"]) {
        if (out.cost[key] === 0)
            delete out.cost[key];
    }
    return out;
}
/** Build + persist the usage record for an org/period, applying the cost model. */
export async function recordUsage(stores, clock, inputs, rates = DEFAULT_COST_RATES) {
    // Conditional spreads, never `key: undefined`: the DynamoDB document client
    // rejects undefined attribute values when it marshals the item.
    const measured = inputs.storageBytes !== undefined || inputs.dedicatedIps !== undefined || inputs.athenaBytesScanned !== undefined;
    const record = {
        orgId: inputs.orgId,
        period: inputs.period,
        emailsSent: inputs.emailsSent,
        ...(inputs.storageBytes === undefined ? {} : { storageBytes: inputs.storageBytes }),
        ...(inputs.dedicatedIps === undefined ? {} : { dedicatedIps: inputs.dedicatedIps }),
        ...(inputs.athenaBytesScanned === undefined ? {} : { athenaBytesScanned: inputs.athenaBytesScanned }),
        ...(inputs.backupBytes === undefined ? {} : { backupBytes: inputs.backupBytes }),
        ...(measured ? { awsFiguresMeasured: true } : {}),
        cost: estimateCost(inputs, rates),
        computedAt: clock.now().toISOString(),
    };
    await stores.usage.put(record);
    return record;
}
/**
 * Meter one org for one period from data this deployment actually holds (#199).
 *
 * The split matters, because the Usage screen previously showed a permanent $0
 * for everything: **email volume** is ours — it comes from the event log — while
 * **storageBytes / dedicatedIps / athenaBytesScanned** come from AWS billing and
 * CloudWatch, which this stack does not read. So those three are CARRIED FORWARD
 * from whatever an operator's metering job last fed in through
 * `usageIngestHandler`, rather than being recomputed as zero.
 *
 * That carry-forward is the whole point of doing this as a merge. A nightly job
 * that wrote `storageBytes: 0` would silently erase the operator's real figures
 * every night, and the screen would go back to reading $0 — the same defect in a
 * more expensive costume.
 *
 * Athena in particular can never be derived here: its CloudWatch metrics are
 * dimensioned by WORKGROUP and there is one workgroup per stage, so per-org
 * attribution is not something the stack can compute at all.
 *
 * Until an ingest has supplied them, those figures are carried as ABSENT, not
 * as 0 (#370), and the screen shows "not measured". Rows written before
 * `awsFiguresMeasured` existed hold zeros this function used to invent as a
 * fallback; a zero on such a row is read as unmeasured, while a nonzero value
 * on it can only have come from an ingest and is carried forward.
 *
 * Email volume counts BOTH kinds of send id (#370): `campaigns.list` returns
 * only `CAMPAIGNREC#` rows, so drip and re-engagement sends — which have no
 * campaign record — were never metered. Ids in both lists (an edition that
 * predates its record) are counted once, as recorded campaigns.
 *
 * Record-less ids are read through the org's time-ordered event window (#320),
 * not one `events.all` per id: drip ids are minted per ENROLLMENT, so an org
 * can hold tens of thousands of them, and a read per id would not fit in the
 * meter's 30-second run. The window misses record-less events written before
 * that index existed; those were never metered at all before this, so it is a
 * floor, not a regression. A store without the window falls back to per id.
 */
export async function meterOrgUsage(stores, clock, orgId, period, rates = DEFAULT_COST_RATES) {
    const [result] = await meterOrgUsagePeriods(stores, clock, orgId, [period], rates);
    if (result.error !== undefined)
        throw new Error(result.error);
    return result.record;
}
/**
 * Meter one org for SEVERAL periods, reading its events once (#370).
 *
 * On the 1st the scheduled run meters two periods. Metering them one at a time
 * read every recorded campaign's full event history (`events.all` is not
 * bounded to a period) twice, inside a 30-second run where a timeout leaves
 * BOTH months stale. Here each history is read once and counted per period,
 * and record-less ids get one window spanning every period asked for.
 *
 * A failed READ throws, since no period can be counted without it. Each
 * period's merge-and-write is then independent: one that fails is reported in
 * its own entry and does not stop the other.
 */
export async function meterOrgUsagePeriods(stores, clock, orgId, periods, rates = DEFAULT_COST_RATES) {
    const sent = await emailsSentByPeriod(stores, orgId, periods);
    const out = [];
    for (const period of periods) {
        try {
            out.push({ period, record: await writeMetered(stores, clock, orgId, period, sent.get(period) ?? 0, rates) });
        }
        catch (e) {
            out.push({ period, error: e.message });
        }
    }
    return out;
}
/** Sends per period across both kinds of send id; see `meterOrgUsage`. */
async function emailsSentByPeriod(stores, orgId, periods) {
    const sent = new Map(periods.map((p) => [p, 0]));
    if (periods.length === 0)
        return sent;
    const count = (events) => {
        for (const e of events) {
            const p = usagePeriodOf(e.at);
            if (e.type === "sent" && sent.has(p))
                sent.set(p, sent.get(p) + 1);
        }
    };
    const recorded = new Set((await stores.campaigns.list(orgId)).map((c) => c.campaignId));
    const recordLess = new Set(((await stores.recordLessSendIds?.(orgId)) ?? []).filter((id) => !recorded.has(id)));
    for (const campaignId of recorded)
        count(await stores.events.all(orgId, campaignId));
    if (recordLess.size > 0) {
        // `-31` is a safe upper DAY for every month: the window is bounded by
        // string comparison and grouped by month, never parsed as a calendar date.
        // "YYYY-MM" periods sort chronologically as strings.
        const sorted = [...periods].sort();
        const windowed = await stores.events.betweenDates?.(orgId, `${sorted[0]}-01`, `${sorted[sorted.length - 1]}-31`);
        if (windowed) {
            count(windowed.filter((e) => recordLess.has(e.campaignId)));
        }
        else {
            for (const campaignId of recordLess)
                count(await stores.events.all(orgId, campaignId));
        }
    }
    return sent;
}
/** Merge a fresh email count with the period's carried-forward AWS-side figures. */
async function writeMetered(stores, clock, orgId, period, emailsSent, rates) {
    const stored = await stores.usage.get(orgId, period);
    // Through the same legacy rule the Usage API reads with, so what is carried
    // forward is exactly what the screen showed as measured.
    const prior = stored ? readUsageRecord(stored) : undefined;
    const storageBytes = prior?.storageBytes;
    const dedicatedIps = prior?.dedicatedIps;
    const athenaBytesScanned = prior?.athenaBytesScanned;
    return recordUsage(stores, clock, {
        orgId,
        period,
        emailsSent,
        ...(storageBytes !== undefined ? { storageBytes } : {}),
        ...(dedicatedIps !== undefined ? { dedicatedIps } : {}),
        ...(athenaBytesScanned !== undefined ? { athenaBytesScanned } : {}),
        ...(prior?.backupBytes !== undefined ? { backupBytes: prior.backupBytes } : {}),
    }, rates);
}
/**
 * The billing periods a scheduled meter run should (re)compute (#370).
 *
 * The meter runs daily at 04:00 UTC and used to meter only the current period,
 * so the last ~20 hours of every month — sends after the final 04:00 run — were
 * never counted: on the 1st it moved straight on to the new month. A run on the
 * 1st (UTC) therefore re-meters the PREVIOUS period as well, which closes it out
 * with its final day included. Re-metering is idempotent: the email count is
 * recomputed from the event log and the AWS-side figures are carried forward.
 */
export function meteringPeriods(now) {
    const current = usagePeriodOf(now.toISOString());
    if (now.getUTCDate() !== 1)
        return [current];
    const previous = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    return [usagePeriodOf(previous.toISOString()), current];
}
//# sourceMappingURL=usage.js.map